/**
 * Given an enum object, returns a collection of string based keys. This can be
 * used to get all keys of an enum object without having to filter the inverse
 * id number key entries.
 * @param obj Enum object to iterate.
 * @returns Collection of string keys.
 */
export function enumStringKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
	return Object.keys(obj).filter((k) => Number.isNaN(Number(k))) as K[];
}

/**
 * Given an enum object, returns a collection of numeric-only values.
 * @param obj Enum object to iterate.
 * @returns Collection of numeric values.
 */
export function enumNumericValues<O extends object>(obj: O): number[] {
	return Object.values(obj).filter((k) => !Number.isNaN(Number(k)));
}

/**
 * Given an enum object, returns a function that can validate if a given string
 * is a valid value for the originating enum.
 * @param enumObj The string enum to create the validator for.
 * @returns A method `(value: string): value is T`, returning `true` if `value` is a valid
 * value of `T` enum, otherwise `false`.
 */
export function buildStringEnumValidator<T extends string>(enumObj: Record<string, T>) {
	const values = Object.values(enumObj) as string[];

	return (value: string): value is T => values.includes(value);
}
